import { useState } from 'react';
import { ArrowBigUp, ArrowBigDown, CheckCircle, MoreVertical, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import MentionTextarea from '../common/MentionTextarea';

export default function PostCard({ post, discussionOwnerId, onUpdate, onDelete, isNested = false }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [showMenu, setShowMenu] = useState(false);
  const { user, token } = useAuthStore();
  
  const userVote = post.user_vote || null;
  const isOwner = user?.id === post.user_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'instructor';
  const isDiscussionOwner = user?.id === discussionOwnerId;

  const handleVote = async (voteValue) => {
    try {
      const response = await axios.post(
        `http://localhost:8000/api/posts/${post.id}/vote`,
        { vote: voteValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onUpdate();
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(
        `http://localhost:8000/api/posts/${post.id}`,
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to edit post:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`http://localhost:8000/api/posts/${post.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onDelete();
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleMarkSolution = async () => {
    try {
      await axios.post(
        `http://localhost:8000/api/posts/${post.id}/solution`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onUpdate();
    } catch (error) {
      console.error('Failed to mark solution:', error);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    
    try {
      await axios.post(
        `http://localhost:8000/api/discussions/${post.discussion_id}/posts`,
        { content: replyContent, parent_id: post.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReplyContent('');
      setShowReplyForm(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to post reply:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`bg-white ${isNested ? 'ml-12 mt-4' : ''} p-4 rounded-lg border border-gray-200`}>
      <div className="flex gap-3">
        {/* Vote buttons */}
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => handleVote(1)}
            className={`p-1 rounded hover:bg-gray-100 ${userVote === 1 ? 'text-indigo-600' : 'text-gray-400'}`}
          >
            <ArrowBigUp className="h-6 w-6" fill={userVote === 1 ? 'currentColor' : 'none'} />
          </button>
          <span className="text-sm font-semibold text-gray-700">
            {post.vote_score || 0}
          </span>
          <button
            onClick={() => handleVote(-1)}
            className={`p-1 rounded hover:bg-gray-100 ${userVote === -1 ? 'text-orange-600' : 'text-gray-400'}`}
          >
            <ArrowBigDown className="h-6 w-6" fill={userVote === -1 ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.name || 'User')}&background=4F46E5&color=fff`}
                alt={post.user?.name}
                className="h-8 w-8 rounded-full"
              />
              <div>
                <p className="font-medium text-gray-900">{post.user?.name}</p>
                <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
              </div>
              {post.is_solution && (
                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  <CheckCircle className="h-3 w-3" />
                  Solution
                </span>
              )}
            </div>

            {/* Menu */}
            {(isOwner || isAdmin) && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <MoreVertical className="h-5 w-5 text-gray-400" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                    {isOwner && (
                      <button
                        onClick={() => { setIsEditing(true); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                    {(isOwner || isAdmin) && (
                      <button
                        onClick={() => { handleDelete(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                    {(isDiscussionOwner || isAdmin) && !isNested && (
                      <button
                        onClick={() => { handleMarkSolution(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {post.is_solution ? 'Unmark' : 'Mark'} as Solution
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Post content */}
          {isEditing ? (
            <div className="mb-3">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleEdit}
                  className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                >
                  Save
                </button>
                <button
                  onClick={() => { setIsEditing(false); setEditContent(post.content); }}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-700 mb-3 whitespace-pre-wrap">{post.content}</p>
          )}

          {/* Reply button */}
          {!isNested && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Reply
            </button>
          )}

          {/* Reply form */}
          {showReplyForm && (
            <div className="mt-3 ml-12">
              <MentionTextarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write your reply... Use @ to mention someone"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleReply}
                  disabled={!replyContent.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Post Reply
                </button>
                <button
                  onClick={() => { setShowReplyForm(false); setReplyContent(''); }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Nested replies */}
          {post.replies && post.replies.length > 0 && (
            <div className="mt-4 space-y-4">
              {post.replies.map((reply) => (
                <PostCard
                  key={reply.id}
                  post={reply}
                  discussionOwnerId={discussionOwnerId}
                  onUpdate={onUpdate}
                  onDelete={onUpdate}
                  isNested={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
